import React, { useState } from "react";
import {
  Database, ShieldAlert, AlertTriangle, Cuboid, AlertOctagon, Tag,
  Image as ImageIcon, FileSpreadsheet, ShieldCheck, BookOpen, History,
  ScrollText, Building2, LibraryBig, Wrench, FileUp, Users, Download,
  ChevronDown, ChevronRight, ArrowRight, ArrowLeft,
} from "lucide-react";

const ICON_MAP = {
  "database": Database, "shield-alert": ShieldAlert, "alert-triangle": AlertTriangle,
  "cuboid": Cuboid, "alert-octagon": AlertOctagon, "tag": Tag,
  "image": ImageIcon, "file-spread": FileSpreadsheet, "shield-check": ShieldCheck,
  "book-open": BookOpen, "history": History, "scroll-text": ScrollText,
  "building": Building2, "library": LibraryBig, "wrench": Wrench,
  "file-up": FileUp, "users": Users, "download": Download,
};

const VIEW_TO_TAB = {
  pdo_json: "pdo", tag_db: "tags", haz_area: "hazarea", qra: "qra",
  model_3d: "model", clashes: "clashes", drawings: "drawings",
  deliverables: "deliverables", validation: "validation",
  standards: "standards", versions: "versions", audit: "audit",
  bep: "bep", symbols: "symbols", engg_backbone: "engineering-backbone",
  imports: "imports", team: "collab", files: "files",
};

/**
 * Collapsible relationship panel for a single L2 stage.
 *
 * Renders four buckets (Stage Predecessors / Views Predecessors /
 * Stage Successors / Views Successors). Reads the stage payload returned
 * by `GET /api/projects/{id}/stages/{code}/deps` (which Wave Y enriches
 * with `views_predecessors` and `views_successors` arrays).
 *
 * Both stage-code chips and view chips are clickable:
 *   - clicking a stage code opens that stage in the workspace
 *   - clicking a view chip switches to that Views Panel tab
 */
export default function StageRelationshipsPanel({
  stageCode,
  hardDeps = [],
  softDeps = [],
  viewsPredecessors = [],
  viewsSuccessors = [],
  stageSuccessors = [],          // computed by parent (catalog-derived)
  onOpenStage,                   // (code) => void
  onOpenView,                    // (tabKey) => void
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);

  const totalCount =
    hardDeps.length + softDeps.length +
    viewsPredecessors.length + viewsSuccessors.length + stageSuccessors.length;

  if (totalCount === 0) return null;

  return (
    <div
      data-testid={`stage-relationships-${stageCode}`}
      style={{
        marginTop: 12,
        border: "1px solid var(--pf-border)",
        borderRadius: 6,
        background: "var(--pf-surface)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`stage-relationships-toggle-${stageCode}`}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          padding: "10px 14px", background: "transparent", border: 0,
          color: "var(--pf-text)", cursor: "pointer", textAlign: "left",
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="pf-overline" style={{ flex: 1 }}>STAGE GATE & VIEW RELATIONSHIPS</span>
        <span style={{ fontSize: 11, color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
          {hardDeps.length + softDeps.length} pred · {stageSuccessors.length} succ ·{" "}
          {viewsPredecessors.length + viewsSuccessors.length} views
        </span>
      </button>
      {open && (
        <div style={{ padding: "4px 14px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Bucket
            title="Stage Predecessors"
            subtitle="must be ready before this stage"
            arrow={<ArrowRight size={11} />}
            empty="None — this is a starting stage"
          >
            {hardDeps.map((c) => (
              <StageChip key={`hd-${c}`} code={c} kind="hard" onClick={() => onOpenStage?.(c)} />
            ))}
            {softDeps.map((c) => (
              <StageChip key={`sd-${c}`} code={c} kind="soft" onClick={() => onOpenStage?.(c)} />
            ))}
          </Bucket>

          <Bucket
            title="Views Predecessors"
            subtitle="referenced WHILE working on this stage"
            arrow={<ArrowRight size={11} />}
            empty="No views read by this stage"
          >
            {viewsPredecessors.map((v) => (
              <ViewChip key={`vp-${v.id}`} view={v} onClick={() => {
                const t = VIEW_TO_TAB[v.id]; if (t) onOpenView?.(t);
              }} />
            ))}
          </Bucket>

          <Bucket
            title="Stage Successors"
            subtitle="downstream stages that consume this output"
            arrow={<ArrowLeft size={11} />}
            empty="None — terminal stage"
          >
            {stageSuccessors.length === 0 ? null : (
              stageSuccessors.map((c) => (
                <StageChip key={`ss-${c}`} code={c} kind="hard" onClick={() => onOpenStage?.(c)} />
              ))
            )}
          </Bucket>

          <Bucket
            title="Views Successors"
            subtitle="views that UPDATE when this stage completes"
            arrow={<ArrowLeft size={11} />}
            empty="No views written by this stage"
          >
            {viewsSuccessors.map((v) => (
              <ViewChip key={`vs-${v.id}`} view={v} onClick={() => {
                const t = VIEW_TO_TAB[v.id]; if (t) onOpenView?.(t);
              }} />
            ))}
          </Bucket>
        </div>
      )}
    </div>
  );
}

function Bucket({ title, subtitle, children, empty }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div>
      <div className="pf-overline" style={{ marginBottom: 4, color: "var(--pf-spark)" }}>{title}</div>
      <div style={{ fontSize: 10.5, color: "var(--pf-text-muted)", marginBottom: 6 }}>{subtitle}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.length > 0 ? items : (
          <span style={{ fontSize: 11.5, color: "var(--pf-text-muted)", fontStyle: "italic" }}>{empty}</span>
        )}
      </div>
    </div>
  );
}

function StageChip({ code, kind, onClick }) {
  const isHard = kind === "hard";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`stage-chip-${code}`}
      title={isHard ? "Hard dependency · click to open" : "Soft dependency · click to open"}
      style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        padding: "4px 8px", borderRadius: 3,
        background: isHard ? "rgba(59,130,246,0.10)" : "rgba(100,116,139,0.10)",
        border: `1px solid ${isHard ? "#3b82f6" : "#64748b"}`,
        color: isHard ? "#7dd3fc" : "#cbd5e1",
        cursor: "pointer",
      }}
    >
      {code}
    </button>
  );
}

function ViewChip({ view, onClick }) {
  const Icon = ICON_MAP[view.icon] || Database;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`view-chip-${view.id}`}
      title={view.blurb}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        fontSize: 11, padding: "4px 9px", borderRadius: 999,
        background: "rgba(30,136,229,0.10)", border: "1px solid #1e88e5",
        color: "#7dd3fc", cursor: "pointer", fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <Icon size={11} strokeWidth={1.7} /> {view.name}
    </button>
  );
}
